<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import evolutionAPI from 'dashboard/api/evolution';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const isLoading = ref(true);
const instanceData = ref(null);
const settings = ref({
  always_online: false,
  reject_call: false,
  read_messages: false,
  ignore_groups: false,
  ignore_status: false,
});
const isSaving = ref(false);

// Connection
const isConnecting = ref(false);
const qrCode = ref(null);
const connectionState = ref(null);
const pairingCode = ref(null);
const pairingPhone = ref('');
const showPairing = ref(false);
let pollInterval = null;

const inboxId = computed(() => props.inbox.id);
const instanceName = computed(
  () => props.inbox.additional_attributes?.evolution_go_instance_name || ''
);
const isConnected = computed(
  () => instanceData.value?.inbox?.state === 'open'
);
const ownerNumber = computed(
  () => instanceData.value?.inbox?.owner_number || ''
);
const profileName = computed(
  () => instanceData.value?.inbox?.profile_name || ''
);
const webhookUrl = computed(
  () => instanceData.value?.webhook?.url || ''
);
const instanceToken = computed(
  () => instanceData.value?.instance_token || ''
);

const fetchInstance = async () => {
  try {
    const { data } = await evolutionAPI.getInstance(inboxId.value);
    instanceData.value = data;
    if (data.settings) {
      settings.value = { ...settings.value, ...data.settings };
    }
  } catch {
    instanceData.value = null;
  } finally {
    isLoading.value = false;
  }
};

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
};

const checkStatus = async () => {
  try {
    const { data } = await evolutionAPI.getStatus(inboxId.value);
    const state = data.instance?.state || data.state;
    connectionState.value = state;
    if (state === 'open' || state === 'connected') {
      stopPolling();
      useAlert('WhatsApp conectado!');
      qrCode.value = null;
      pairingCode.value = null;
      isConnecting.value = false;
      connectionState.value = null;
      await fetchInstance();
    }
  } catch {
    /* keep polling */
  }
};

const fetchQrCode = async () => {
  try {
    const { data } = await evolutionAPI.getQrCode(inboxId.value);
    if (data.base64) qrCode.value = data.base64;
  } catch {
    /* QR not ready */
  }
};

const startPolling = () => {
  stopPolling();
  pollInterval = setInterval(() => {
    checkStatus();
    if (!showPairing.value) fetchQrCode();
  }, 5000);
};

const connectWhatsApp = async () => {
  isConnecting.value = true;
  qrCode.value = null;
  pairingCode.value = null;
  connectionState.value = null;
  showPairing.value = false;

  try {
    const { data } = await evolutionAPI.connectInbox(inboxId.value);
    if (data.base64) qrCode.value = data.base64;
    startPolling();
  } catch (e) {
    useAlert(e.response?.data?.error || 'Erro ao conectar');
    isConnecting.value = false;
  }
};

const requestPairingCode = async () => {
  const phone = pairingPhone.value.replace(/\D/g, '');
  if (!phone) {
    useAlert('Digite o número do telefone');
    return;
  }

  try {
    const { data } = await evolutionAPI.pair(inboxId.value, phone);
    pairingCode.value = data.pairing_code;
    showPairing.value = true;
    startPolling();
  } catch (e) {
    useAlert(e.response?.data?.error || 'Erro ao gerar código');
  }
};

const cancelConnect = () => {
  stopPolling();
  isConnecting.value = false;
  qrCode.value = null;
  pairingCode.value = null;
  connectionState.value = null;
  showPairing.value = false;
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    await evolutionAPI.updateSettings(inboxId.value, settings.value);
    useAlert('Configurações salvas!');
  } catch {
    useAlert('Erro ao salvar configurações');
  } finally {
    isSaving.value = false;
  }
};

// Disconnect — keeps session, can reconnect without QR
const disconnectWhatsApp = async () => {
  try {
    await evolutionAPI.disconnect(inboxId.value);
    useAlert('WhatsApp desconectado (sessão mantida)');
    await fetchInstance();
  } catch {
    useAlert('Erro ao desconectar');
  }
};

// Logout — removes session, needs new QR scan
const logoutWhatsApp = async () => {
  try {
    await evolutionAPI.logout(inboxId.value);
    useAlert('WhatsApp deslogado (sessão removida)');
    await fetchInstance();
  } catch {
    useAlert('Erro ao deslogar');
  }
};

onMounted(fetchInstance);
onUnmounted(stopPolling);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="flex flex-col gap-6 max-w-2xl py-4">
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner :size="32" class="text-n-slate-10" />
    </div>

    <template v-else>
      <!-- Instance Info -->
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-semibold text-n-slate-12">
          WhatsApp — {{ instanceName }}
        </h2>
        <p class="text-sm text-n-slate-10">
          Gerencie a conexão WhatsApp desta caixa de entrada
        </p>
      </div>

      <!-- Connection Status -->
      <div
        class="flex items-center justify-between p-4 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center gap-3">
          <div
            class="size-10 rounded-full flex items-center justify-center"
            :class="
              isConnected
                ? 'bg-green-500/10 text-green-500'
                : 'bg-red-500/10 text-red-400'
            "
          >
            <span
              :class="isConnected ? 'i-lucide-wifi' : 'i-lucide-wifi-off'"
              class="size-5"
            />
          </div>
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ isConnected ? 'Conectado' : 'Desconectado' }}
            </p>
            <p
              v-if="isConnected && ownerNumber"
              class="text-xs text-n-slate-10"
            >
              {{ profileName ? `${profileName} · ` : '' }}+{{ ownerNumber }}
            </p>
          </div>
        </div>
        <button
          v-if="!isConnected && !isConnecting"
          class="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
          @click="connectWhatsApp"
        >
          Conectar WhatsApp
        </button>
      </div>

      <!-- QR Code -->
      <div
        v-if="isConnecting && !showPairing"
        class="flex flex-col items-center gap-4 p-6 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          Escaneie o QR Code
        </h3>
        <p class="text-xs text-n-slate-10 text-center">
          WhatsApp → Dispositivos conectados → Conectar dispositivo
        </p>
        <div v-if="qrCode" class="p-4 bg-white rounded-xl">
          <img
            :src="
              qrCode.startsWith('data:')
                ? qrCode
                : `data:image/png;base64,${qrCode}`
            "
            alt="QR Code"
            class="w-64 h-64"
          />
        </div>
        <div
          v-else
          class="flex items-center justify-center w-64 h-64 bg-n-alpha-1 rounded-xl"
        >
          <Spinner :size="32" class="text-n-slate-10" />
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-10">
          <Spinner :size="14" />
          <span>Aguardando leitura do QR Code...</span>
        </div>
        <div class="flex gap-2">
          <button
            class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
            @click="
              showPairing = true;
              qrCode = null;
            "
          >
            Usar código numérico
          </button>
          <button
            class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
            @click="cancelConnect"
          >
            Cancelar
          </button>
        </div>
      </div>

      <!-- Pairing Code (alternative to QR) -->
      <div
        v-if="isConnecting && showPairing"
        class="flex flex-col items-center gap-4 p-6 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          Conectar com código numérico
        </h3>
        <div v-if="!pairingCode" class="flex flex-col items-center gap-3">
          <p class="text-xs text-n-slate-10 text-center">
            Digite o número do WhatsApp (com DDD e código do país)
          </p>
          <input
            v-model="pairingPhone"
            type="text"
            placeholder="5521999999999"
            class="w-64 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-center outline-none focus:ring-2 focus:ring-n-brand"
            @keyup.enter="requestPairingCode"
          />
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90"
            @click="requestPairingCode"
          >
            Gerar código
          </button>
        </div>
        <div v-else class="flex flex-col items-center gap-3">
          <p class="text-xs text-n-slate-10 text-center">
            Digite este código no WhatsApp → Dispositivos conectados → Conectar
            com número de telefone
          </p>
          <p
            class="text-3xl font-mono font-bold text-n-slate-12 tracking-widest"
          >
            {{ pairingCode }}
          </p>
          <div class="flex items-center gap-2 text-xs text-n-slate-10">
            <Spinner :size="14" />
            <span>Aguardando conexão...</span>
          </div>
        </div>
        <div class="flex gap-2">
          <button
            class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
            @click="
              showPairing = false;
              pairingCode = null;
            "
          >
            Usar QR Code
          </button>
          <button
            class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
            @click="cancelConnect"
          >
            Cancelar
          </button>
        </div>
      </div>

      <!-- Instance Info Card (when connected) -->
      <div
        v-if="isConnected"
        class="flex flex-col gap-2 p-5 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
          Informações da Instância
        </h3>
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p class="text-xs text-n-slate-9">Token da Instância</p>
            <p class="text-n-slate-12 font-mono text-xs truncate">
              {{ instanceToken }}
            </p>
          </div>
          <div>
            <p class="text-xs text-n-slate-9">Webhook</p>
            <p class="text-n-slate-12 font-mono text-xs truncate">
              {{ webhookUrl }}
            </p>
          </div>
        </div>
      </div>

      <!-- Settings (when connected) -->
      <div
        v-if="isConnected"
        class="flex flex-col gap-4 p-5 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">Configurações</h3>

        <label class="flex items-center justify-between cursor-pointer">
          <div>
            <p class="text-sm text-n-slate-12">Ignorar Grupos</p>
            <p class="text-xs text-n-slate-9">
              Não processar mensagens de grupos
            </p>
          </div>
          <input
            v-model="settings.ignore_groups"
            type="checkbox"
            class="toggle toggle-sm"
          />
        </label>

        <label class="flex items-center justify-between cursor-pointer">
          <div>
            <p class="text-sm text-n-slate-12">Visualizar Mensagens</p>
            <p class="text-xs text-n-slate-9">
              Marcar todas as mensagens como lidas
            </p>
          </div>
          <input
            v-model="settings.read_messages"
            type="checkbox"
            class="toggle toggle-sm"
          />
        </label>

        <label class="flex items-center justify-between cursor-pointer">
          <div>
            <p class="text-sm text-n-slate-12">Rejeitar Chamadas</p>
            <p class="text-xs text-n-slate-9">
              Rejeitar ligações automaticamente
            </p>
          </div>
          <input
            v-model="settings.reject_call"
            type="checkbox"
            class="toggle toggle-sm"
          />
        </label>

        <label class="flex items-center justify-between cursor-pointer">
          <div>
            <p class="text-sm text-n-slate-12">Sempre Online</p>
            <p class="text-xs text-n-slate-9">
              Manter status sempre online no WhatsApp
            </p>
          </div>
          <input
            v-model="settings.always_online"
            type="checkbox"
            class="toggle toggle-sm"
          />
        </label>

        <label class="flex items-center justify-between cursor-pointer">
          <div>
            <p class="text-sm text-n-slate-12">Ignorar Status</p>
            <p class="text-xs text-n-slate-9">
              Não processar status/stories do WhatsApp
            </p>
          </div>
          <input
            v-model="settings.ignore_status"
            type="checkbox"
            class="toggle toggle-sm"
          />
        </label>

        <button
          class="self-start px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          :disabled="isSaving"
          @click="saveSettings"
        >
          <Spinner v-if="isSaving" :size="14" />
          Salvar configurações
        </button>
      </div>

      <!-- Danger Zone (when connected) -->
      <div
        v-if="isConnected"
        class="flex flex-col gap-3 p-5 rounded-xl border border-red-500/20 bg-red-500/5"
      >
        <h3 class="text-sm font-semibold text-red-400">Zona de Perigo</h3>
        <div class="flex gap-3">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 transition-colors"
            @click="disconnectWhatsApp"
          >
            Desconectar (manter sessão)
          </button>
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
            @click="logoutWhatsApp"
          >
            Deslogar (remover sessão)
          </button>
        </div>
        <p class="text-xs text-n-slate-10">
          <strong>Desconectar:</strong> mantém a sessão, pode reconectar sem QR.
          <strong>Deslogar:</strong> remove a sessão, precisa escanear QR de
          novo.
        </p>
      </div>
    </template>
  </div>
</template>
